/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import ko from "knockout";
import birdata from "../../api.js";
import _ from "underscore";
import util from "../../util/util.js";
import moment from "dayjs";
import Papa from "papaparse";
import stream from "../../util/stream.js";
import SortableTableViewModel from "../shared/sortable-table.js";
import SurveyImporter from "./import/survey.js";
import SharedSiteImporter from "./import/shared-site.js";
import AreaImporter from "./import/area.js";
import RegionImporter from "./import/region.js";

function FileModel(params) {
	var self = this;

	if(params.file) {
		this.file = params.file;
		this.name = params.file.name;
		this.size = params.file.size;
		this.uuid = ko.observable();
	} else {
		this.name = params.name;
		this.size = params.size;
		this.uuid = ko.observable(params.uuid);
	}

	this.formattedSize = util.formatSize(this.size);

	this.message = ko.observable('');
	this.uploadProgress = ko.observable();
	this.uploadPercent = ko.pureComputed(function() {
		return (self.uploadProgress() * 100).toFixed(0) + "%";
	});
	this.uploadStatus = ko.observable();
	this.url = ko.pureComputed(function() {
		return self.uuid() ? birdata.uploadURL(self.uuid()) : null;
	});
}

function ImportDetailViewModel(params) {
	var self = this;

	self.loaded = ko.observable(false);
	self.id = ko.observable();

	var abort = false;
	self.close = function() {
		abort = true; // Stop any running imports
		params.finished();
	}

	var importJSON = (params && params.id) ? birdata.dataImport(params.id) : Promise.resolve(null);

	birdata.refreshLookups();

	Promise.all([
		birdata.lookup(),
		birdata.speciesList({ supertaxa: 'all' }),
		importJSON
	]).then(function([lookups, species, json]) {
		self.selectedFiles = ko.observableArray([]);
		self.uploadingFiles = ko.pureComputed(function() {
			return _.filter(self.selectedFiles(), function(f) {
				return f.uploadStatus() == 'uploading' || f.uploadStatus() == 'finishing';
			});
		});
		self.preUploadIssues = ko.observable();
		self.status = ko.observable('init');

		self.tabs = ["Options", "Files", "Results"];
		self.currentTab = ko.observable("Options");

		self.nextTab = function() {
			var index = self.tabs.indexOf(self.currentTab());
			index = Math.min(index + 1, self.tabs.length - 1);
			self.currentTab(self.tabs[index]);
		}

		self.showNextButton = ko.pureComputed(function() {
			switch(self.currentTab()) {
				case "Options":
					return self.showFileSelect();
				case "Files":
					return self.showUpload();
				case "Results":
				default:
					return false;
			}
			var index = self.tabs.indexOf(self.currentTab());
			return index < self.tabs.length - 1;
		});

		self.importTypes = [
			{ name: 'Surveys - General Birdata', code: 'surveys-birdata' },
			{ name: 'Surveys - SOP', code: 'surveys-sop' },
			{ name: 'Surveys - Shorebird', code: 'surveys-s2020' },
			{ name: 'Surveys - Beach-nesting Birds', code: 'surveys-bnb' },
			{ name: 'Surveys - Aussie Bird Count', code: 'surveys-abc' },
			{ name: 'Shared Site Layer', code: 'shared-sites' },
			{ name: 'Area Layer', code: 'areas' },
			{ name: 'Regional Groups', code: 'regions' }
		];
		self.importType = ko.observable('');

		self.importer = ko.pureComputed(function() {
			if(!self.importType()) {
				return null;
			}

			switch(self.importType().code) {
				case 'surveys-birdata':
				case 'surveys-sop':
				case 'surveys-s2020':
				case 'surveys-bnb':
				case 'surveys-abc':
					return new SurveyImporter({
						lookups: lookups,
						species: species,
						code: self.importType().code
					});
				case 'shared-sites':
					return new SharedSiteImporter({
						lookups: lookups
					});
				case 'areas':
					return new AreaImporter();
				case 'regions':
					return new RegionImporter();
			}
		});

		self.importSpecificationURL = ko.pureComputed(function() {
			if(self.importer()) {
				return self.importer().importSpecificationURL;
			}
		});

		self.selectFile = function() {
			util.selectFiles(self.importer().selectFileOptions).then(function(files) {
				var all = self.selectedFiles();
				for(var i = 0; i < files.length; i++) {
					all.push(new FileModel({ file: files[i]}));
				}
				self.selectedFiles(all);
			});
		};

		self.showFileSelect = ko.pureComputed(function() {
			if(self.importer()) {
				return self.importer().showFileSelect();
			} else {
				return false;
			}
		});

		self.showIssues = ko.pureComputed(function() {
			return self.showFileSelect() && self.selectedFiles().length > 0 && _.all(self.selectedFiles(), function(f) { return !!f.file; });
		});

		self.warningsOrErrors = ko.pureComputed(function() {
			return _.some(self.preUploadIssues() || [], function(issue) { return issue.type == "Error" || issue.type == "Warning"; });
		})

		self.showUpload = ko.pureComputed(function() {
			return self.selectedFiles().length > 0 && !_.any(self.preUploadIssues, function(issue) { return issue.type == "Error"; });
		});

		ko.computed(function() {
			if(self.importer()) {
				self.importer().checkFiles(self.importType(), self.selectedFiles()).then(self.preUploadIssues);
			} else {
				self.preUploadIssues([]);
			}
		});

		self.canEdit = ko.pureComputed(function() {
			switch(self.status()) {
				case 'init':
				case 'checked_ok':
				case 'checked_error':
				case 'import_error':
				case 'error':
					return true;
				default:
					return false;
			}
		});

		self.finishImport = function() {
			if(self.status() != 'checked_ok') {
				console.log("Invalid status to finish import: " + self.status());
				return;
			}

			self.status('processing');
			trackProcessing(birdata.updateImport(self.id(), {complete:true}));
		}

		self.processImport = function() {
			switch(self.status) {
				case 'pending_check':
				case 'pending_import':
				case 'checking':
				case 'importing':
					console.log("Already processing import");
					return;
			}

			self.issues([]);

			var files = self.selectedFiles();
			var filesToUpload = _.filter(files, function(f) { return !f.uuid(); });

			self.status('uploading');
			var uploads = _.chain(files)
				.filter(function(f) { return !f.uuid(); }) // Only upload files that aren't already uploaded
				.map(function(f) {
					return birdata.remoteUpload(f.file, f.uploadStatus, f.uploadProgress, { compress: shouldCompress(f) }).then(function(url) {
						f.uuid(url.match("/([^/]*)$")[1]);
						return url;
					})
				})
				.value();

			trackProcessing(
				Promise.all(uploads).then(function(urls) {
					if(abort) return;
					self.status('processing');
					var meta = importMeta();
					lastImportMeta(meta);
					return self.id() ?
						birdata.updateImport(self.id(), meta) :
						birdata.createImport(meta);
				}));
		};

		function shouldCompress(file) {
			return !file.name.endsWith(".zip");
		}

		function trackProcessing(importPromise) {
			importPromise.then(function(result) {
				if(abort) return;
				console.log(result);
				self.status('processing');
				if(result.id) {
					self.id(result.id);
				}
				return stream.each(
					stream.split(birdata.importProgress(self.id()), '\n'),
					handleProgressUpdate);
			}).catch(function(e) {
				console.log(e);
				self.status('error');
			});
		}

		var lastImportMeta = ko.observable();
		function importMeta() {
			var meta = {
				type: self.importType().code,
				files: _.map(self.selectedFiles(), function(f) {
					return {
						uuid: f.uuid(),
						name: f.name,
						size: f.size
					};
				})
			};

			if(self.importer()) {
				self.importer().updateMeta(meta);
			}

			return meta;
		}

		self.edited = ko.pureComputed(function() {
			return JSON.stringify(lastImportMeta()) != JSON.stringify(importMeta());
		});

		self.issues = ko.observable([]);

		function loadIssues(id) {
			birdata.importIssues(id).then(function(issues) {
				var iss = [];
				Papa.parse(issues, {
					header: true,
					skipEmptyLines: true,
					step: function(results, parser) {
						iss.push(results.data[0]);
					},
					complete: function() {
						self.issues(iss);
					}
				});
			});
		}

		function handleProgressUpdate(data) {
			if(abort) throw new Error("Processing aborted");
			console.log(data);
			if(!data) return;
			data = JSON.parse(data);

			self.status(data.status);

			if(data.progress !== undefined) {
				self.processProgress(data.progress);
				self.processProgressPercent((data.progress.toFixed(0) + "%"));
			}

			if(data.elapsedSeconds !== undefined) {
				self.processTimeElapsed(util.formatDuration(data.elapsedSeconds, 'seconds'));

				if(data.progress) {
					self.processTimeRemaining(
						util.formatDuration(
							Math.floor(data.elapsedSeconds / data.progress - data.elapsedSeconds), 'seconds'));
				}
			}
		}

		ko.computed(function() {
			console.log(self.id(), self.status());
			if(self.id()) {
				switch(self.status()) {
					case "checked_ok":
					case "checked_error":
					case "imported":
					case "import_error":
						loadIssues(self.id());
				}
			}
		});

		self.processProgress = ko.observable();
		self.processProgressPercent = ko.observable();
		self.processTimeElapsed = ko.observable();
		self.processTimeRemaining = ko.observable();

		if(json) {
			self.importType(_.findWhere(self.importTypes, { code: json.meta.type }));
			self.status(json.status);
			self.id(json.id);
			loadIssues(json.id);
			self.selectedFiles(
				_.map(json.meta.files, function(f) {
					return new FileModel(f);
				})
			);

			if(self.importer()) {
				self.importer().loadMeta(json).then(function() {
					lastImportMeta(importMeta(json));
				});
			} else {
				lastImportMeta(importMeta(json));
			}

			if(json.status == 'checking' || json.status == 'importing') {
				trackProcessing(Promise.resolve(json));
			}
		}

		self.loaded(true);
	});
}

function ImportDataViewModel(params) {
	var self = this;

	self.loaded = ko.observable(false);
	self.errorMessage = ko.observable();
	self.imports = ko.observable([]);

	self.importTable = new SortableTableViewModel([
		{
			name: 'Type',
			value: function(i) {
				var type = i.meta.type;
				switch(type) {
					case 'shared-sites':
					case 'areas':
						return type + " (" + i.meta.layer + ")";
					default:
						return type;
				}
			},
			formatted: function(i) { return this.value(i); },
			id: 'type'
		}, {
			name: 'User',
			value: function(i) { return i.user || 'Unknown'; },
			formatted: function(i) { return this.value(i); },
			id: 'user'
		}, {
			name: 'Date',
			value: function(i) { return i.created_at; },
			formatted: function(i) { return self.formatDate(i.created_at); },
			id: 'date'
		}, {
			name: 'Status',
			value: function(i) { return i.status; },
			formatted: function(i) { return this.value(i); },
			id: 'program'
		}
	]);

	self.importTable.sortDescriptors([{
		column: 'date',
		ascending: false
	}]);

	self.sortedImports = self.importTable.sortedItems(self.imports);

	self.clickTable = function(vm, evt) {
		var i = self.importTable.eventRowIndex(evt);

		if(i !== null) {
			var imp = self.sortedImports()[i];
			if(imp) {
				self.openImport(imp);
			}
		}
	}

	Promise.all([birdata.imports()]).then(function([imports]) {
		self.imports(imports);
		self.currentImport = ko.observable();

		self.newImport = function() {
			self.openImport();
		}

		self.openImport = function(dataImport) {
			var viewModel = new ImportDetailViewModel({
				finished: self.closeImport,
				id: dataImport ? dataImport.id : null
			});
			self.currentImport(viewModel);
		}

		self.closeImport = function() {
			self.currentImport(null);
			birdata.imports().then(self.imports);
		}

		self.formatDate = function(date) {
			return moment(date).format('MMM D, YYYY h:mm A');
		}

		self.loaded(true);
	}).catch(function(e) {
		if(e.xhr && e.xhr.status == 403) {
			self.errorMessage("Access Denied");
		} else {
			throw e;
		}
	});
}

ko.components.register('import-data', {
	viewModel: function(params) {
		return new ImportDataViewModel(params);
	},
	template: require('../../../templates/admin/import-data.html')
});
